import { useEffect, useMemo } from 'react';

import { useQueryClient } from '@mint-lib/query-client';
import { Suggestions } from '@mint-sdk/api-recommendations';
import { useAtom, useSetAtom } from 'jotai';

import {
  useInfiniteSuggestionQuery,
  useReadSuggestion,
} from '../queries/suggestions.js';
import {
  activeSuggestionTabAtom,
  selectedSuggestionAtom,
} from '../store/index.js';
import { SuggestionFilterActions } from '../store/suggestion/suggestionFilter.js';
import { suggestionTabs } from '../utils/getTabs.js';
import { useSuggestionFilter } from './useSuggestionFilter.js';

export const useSyncedSelectedItem = () => {
  const queryClient = useQueryClient();

  const { selectedSuggestionFilter, setSuggestionFilter } =
    useSuggestionFilter();
  const setActiveTab = useSetAtom(activeSuggestionTabAtom);
  const { data: suggestions } = useInfiniteSuggestionQuery({
    ...selectedSuggestionFilter,
  });

  const { mutate: readSuggestion } = useReadSuggestion(
    selectedSuggestionFilter,
  );

  const [selectedSuggestion, setSuggestion] = useAtom(selectedSuggestionAtom);

  const suggestionList = useMemo(
    () => suggestions?.pages.flatMap((p) => p.items) ?? [],
    [suggestions],
  );

  useEffect(() => {
    if (
      suggestionList &&
      selectedSuggestion &&
      (selectedSuggestion.status === Suggestions.Statuses.ACTIVE ||
        selectedSuggestion.status === Suggestions.Statuses.REWORKING ||
        selectedSuggestion.status === Suggestions.Statuses.ACCEPTING)
    ) {
      const currentId = selectedSuggestion.originalId ?? selectedSuggestion.id;
      //FYI: We need to sync suggestion after Reject/Accept/Rework actions
      const currentSuggestion = suggestionList.find(
        (s) => currentId && (s.id === currentId || s.originalId === currentId),
      );

      if (currentSuggestion) {
        setSuggestion(currentSuggestion);
        if (currentSuggestion.notRead) {
          readSuggestion(currentSuggestion);
        }
      } else {
        syncSelectedSuggestion(selectedSuggestion.id);
      }
    }

    async function syncSelectedSuggestion(id: number) {
      const freshSuggestion = await queryClient.fetchQuery(
        Suggestions.getSuggestion({ urlParams: { id } }),
      );

      if (!freshSuggestion) return;

      setSuggestion(freshSuggestion);
      if (freshSuggestion.notRead) {
        readSuggestion(freshSuggestion);
      }

      if (freshSuggestion?.status !== Suggestions.Statuses.ACTIVE) {
        setActiveTab(suggestionTabs[freshSuggestion.status]);
        setSuggestionFilter({
          type: SuggestionFilterActions.RESET,
          data: suggestionTabs[freshSuggestion.status].toUpperCase(),
        });
      }
    }
  }, [
    selectedSuggestion,
    selectedSuggestionFilter,
    suggestionList,
    queryClient,
    readSuggestion,
    setActiveTab,
    setSuggestion,
    setSuggestionFilter,
  ]);
};
